/**
 * Floor Plan Editor Component
 *
 * 2D floor plan editor for drawing zone footprints with snap-to-grid functionality.
 * Supports polygon and rectangle drawing tools.
 */

import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useGeometryEditStore, useDrawingState } from '../../geometryEditStore';
import { useGeometryStore } from '../../geometryStore';
import type { Point2D } from '../../editingTypes';
import type { ZoneGeometry } from '../../types';

/**
 * Props for FloorPlanEditor.
 */
interface FloorPlanEditorProps {
  /** Called when a footprint is completed */
  onFootprintComplete?: (vertices: Point2D[]) => void;
  /** The floor level Z coordinate */
  floorZ?: number;
}

// Canvas configuration
const GRID_SUBDIVISIONS = 4;
const VERTEX_RADIUS = 6;

/**
 * Floor Plan Editor Component.
 */
export function FloorPlanEditor({ onFootprintComplete, floorZ = 0 }: FloorPlanEditorProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Store state
  const drawing = useDrawingState();
  const geometry = useGeometryStore((s) => s.geometry);
  const drawingTool = useGeometryEditStore((s) => s.drawingTool);
  const snapSettings = useGeometryEditStore((s) => s.snapSettings);
  const floorLevels = useGeometryEditStore((s) => s.floorLevels);
  const activeFloorIndex = useGeometryEditStore((s) => s.activeFloorIndex);

  // Store actions
  const setDrawingTool = useGeometryEditStore((s) => s.setDrawingTool);
  const startDrawing = useGeometryEditStore((s) => s.startDrawing);
  const addDrawingVertex = useGeometryEditStore((s) => s.addDrawingVertex);
  const updateDrawingPreview = useGeometryEditStore((s) => s.updateDrawingPreview);
  const closeDrawing = useGeometryEditStore((s) => s.closeDrawing);
  const cancelDrawing = useGeometryEditStore((s) => s.cancelDrawing);
  const setSnapSettings = useGeometryEditStore((s) => s.setSnapSettings);

  // Local state
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
  const [viewOffset, setViewOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(20); // pixels per meter
  const [isPanning, setIsPanning] = useState(false);
  const [lastPanPos, setLastPanPos] = useState({ x: 0, y: 0 });

  // Touch state for gesture handling
  const [touchState, setTouchState] = useState<{
    startTouches: Array<{ x: number; y: number }>;
    lastTouches: Array<{ x: number; y: number }>;
    initialScale: number;
    initialOffset: { x: number; y: number };
  } | null>(null);

  // Get existing zones at the current floor level
  const floorZones = useMemo(() => {
    if (!geometry) return [];
    const tolerance = 0.5;
    return geometry.zones.filter((zone) => {
      const minZ = Math.min(...zone.surfaces.flatMap((s) => s.vertices.map((v) => v.z)));
      return Math.abs(minZ - floorZ) < tolerance;
    });
  }, [geometry, floorZ]);

  // Convert screen coordinates to world coordinates
  const screenToWorld = useCallback(
    (screenX: number, screenY: number): Point2D => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const centerX = canvas.width / 2 + viewOffset.x;
      const centerY = canvas.height / 2 + viewOffset.y;

      return {
        x: (screenX - centerX) / scale,
        y: -(screenY - centerY) / scale, // Flip Y axis
      };
    },
    [scale, viewOffset]
  );

  // Convert world coordinates to screen coordinates
  const worldToScreen = useCallback(
    (worldX: number, worldY: number): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };

      const centerX = canvas.width / 2 + viewOffset.x;
      const centerY = canvas.height / 2 + viewOffset.y;

      return {
        x: centerX + worldX * scale,
        y: centerY - worldY * scale, // Flip Y axis
      };
    },
    [scale, viewOffset]
  );

  // Snap point to grid/vertex/edge
  const snapPoint = useCallback(
    (point: Point2D): Point2D => {
      let snapped = { ...point };

      if (snapSettings.grid) {
        const gridSize = snapSettings.gridSize;
        snapped.x = Math.round(snapped.x / gridSize) * gridSize;
        snapped.y = Math.round(snapped.y / gridSize) * gridSize;
      }

      if (snapSettings.vertex && floorZones.length > 0) {
        // Snap to existing zone vertices
        let minDist = snapSettings.vertexDistance;
        for (const zone of floorZones) {
          for (const surface of zone.surfaces) {
            for (const vertex of surface.vertices) {
              const dist = Math.sqrt(
                Math.pow(point.x - vertex.x, 2) + Math.pow(point.y - vertex.y, 2)
              );
              if (dist < minDist) {
                minDist = dist;
                snapped = { x: vertex.x, y: vertex.y };
              }
            }
          }
        }
      }

      // Snap to drawing vertices
      if (snapSettings.vertex && drawing.vertices.length > 0) {
        let minDist = snapSettings.vertexDistance;
        for (const vertex of drawing.vertices) {
          const dist = Math.sqrt(
            Math.pow(point.x - vertex.x, 2) + Math.pow(point.y - vertex.y, 2)
          );
          if (dist < minDist) {
            minDist = dist;
            snapped = { ...vertex };
          }
        }
      }

      return snapped;
    },
    [snapSettings, floorZones, drawing.vertices]
  );

  // Handle resize
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setCanvasSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Draw the canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    drawGrid(ctx, canvas.width, canvas.height);

    // Draw existing zones
    drawExistingZones(ctx, floorZones);

    // Draw current drawing
    if (drawing.vertices.length > 0) {
      drawPolygon(ctx, drawing.vertices, drawing.currentPosition, !drawing.isClosed);
    }

    // Draw axes
    drawAxes(ctx, canvas.width, canvas.height);
  }, [canvasSize, scale, viewOffset, drawing, floorZones, worldToScreen]);

  // Draw grid
  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const gridSize = snapSettings.gridSize;
    const centerX = width / 2 + viewOffset.x;
    const centerY = height / 2 + viewOffset.y;

    // Calculate visible area in world coordinates
    const worldLeft = -centerX / scale;
    const worldRight = (width - centerX) / scale;
    const worldTop = centerY / scale;
    const worldBottom = -(height - centerY) / scale;

    // Draw minor grid lines
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 0.5;
    ctx.beginPath();

    const minorGridSize = gridSize / GRID_SUBDIVISIONS;
    for (let x = Math.floor(worldLeft / minorGridSize) * minorGridSize; x <= worldRight; x += minorGridSize) {
      const screenX = worldToScreen(x, 0).x;
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, height);
    }
    for (let y = Math.floor(worldBottom / minorGridSize) * minorGridSize; y <= worldTop; y += minorGridSize) {
      const screenY = worldToScreen(0, y).y;
      ctx.moveTo(0, screenY);
      ctx.lineTo(width, screenY);
    }
    ctx.stroke();

    // Draw major grid lines
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.beginPath();

    for (let x = Math.floor(worldLeft / gridSize) * gridSize; x <= worldRight; x += gridSize) {
      const screenX = worldToScreen(x, 0).x;
      ctx.moveTo(screenX, 0);
      ctx.lineTo(screenX, height);
    }
    for (let y = Math.floor(worldBottom / gridSize) * gridSize; y <= worldTop; y += gridSize) {
      const screenY = worldToScreen(0, y).y;
      ctx.moveTo(0, screenY);
      ctx.lineTo(width, screenY);
    }
    ctx.stroke();
  };

  // Draw axes
  const drawAxes = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const origin = worldToScreen(0, 0);

    // X axis (red)
    ctx.strokeStyle = '#ef4444';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, origin.y);
    ctx.lineTo(width, origin.y);
    ctx.stroke();

    // Y axis (green)
    ctx.strokeStyle = '#22c55e';
    ctx.beginPath();
    ctx.moveTo(origin.x, 0);
    ctx.lineTo(origin.x, height);
    ctx.stroke();

    // Origin label
    ctx.fillStyle = '#64748b';
    ctx.font = '12px sans-serif';
    ctx.fillText('0', origin.x + 5, origin.y - 5);
  };

  // Draw existing zones
  const drawExistingZones = (ctx: CanvasRenderingContext2D, zones: ZoneGeometry[]) => {
    for (const zone of zones) {
      // Extract floor surfaces for 2D outline
      const floorSurfaces = zone.surfaces.filter(
        (s) => s.type === 'floor' && Math.abs(s.vertices[0]?.z ?? 0 - floorZ) < 0.5
      );

      for (const surface of floorSurfaces) {
        const screenVertices = surface.vertices.map((v) => worldToScreen(v.x, v.y));

        if (screenVertices.length < 3) continue;

        // Fill
        ctx.fillStyle = 'rgba(59, 130, 246, 0.2)';
        ctx.beginPath();
        ctx.moveTo(screenVertices[0]?.x ?? 0, screenVertices[0]?.y ?? 0);
        for (let i = 1; i < screenVertices.length; i++) {
          ctx.lineTo(screenVertices[i]?.x ?? 0, screenVertices[i]?.y ?? 0);
        }
        ctx.closePath();
        ctx.fill();

        // Stroke
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Zone label
        const centroid = calculateCentroid(screenVertices);
        ctx.fillStyle = '#1e40af';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(zone.name, centroid.x, centroid.y);
        ctx.textAlign = 'start';
      }
    }
  };

  // Draw polygon being drawn
  const drawPolygon = (
    ctx: CanvasRenderingContext2D,
    vertices: Point2D[],
    currentPos: Point2D | null,
    isDrawing: boolean
  ) => {
    if (vertices.length === 0) return;

    const screenVertices = vertices.map((v) => worldToScreen(v.x, v.y));

    // Fill (if closed)
    if (!isDrawing && vertices.length >= 3) {
      ctx.fillStyle = 'rgba(34, 197, 94, 0.2)';
      ctx.beginPath();
      ctx.moveTo(screenVertices[0]?.x ?? 0, screenVertices[0]?.y ?? 0);
      for (let i = 1; i < screenVertices.length; i++) {
        ctx.lineTo(screenVertices[i]?.x ?? 0, screenVertices[i]?.y ?? 0);
      }
      ctx.closePath();
      ctx.fill();
    }

    // Draw lines
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(screenVertices[0]?.x ?? 0, screenVertices[0]?.y ?? 0);
    for (let i = 1; i < screenVertices.length; i++) {
      ctx.lineTo(screenVertices[i]?.x ?? 0, screenVertices[i]?.y ?? 0);
    }

    // Preview line to cursor
    if (isDrawing && currentPos) {
      const screenCurrent = worldToScreen(currentPos.x, currentPos.y);
      ctx.lineTo(screenCurrent.x, screenCurrent.y);

      // Show closing line if near first vertex
      const firstScreen = screenVertices[0];
      if (vertices.length >= 2 && firstScreen) {
        const distToFirst = Math.sqrt(
          Math.pow(screenCurrent.x - firstScreen.x, 2) +
            Math.pow(screenCurrent.y - firstScreen.y, 2)
        );
        if (distToFirst < 20) {
          ctx.setLineDash([5, 5]);
          ctx.lineTo(firstScreen.x, firstScreen.y);
          ctx.setLineDash([]);
        }
      }
    }

    if (!isDrawing && vertices.length >= 3) {
      ctx.closePath();
    }
    ctx.stroke();

    // Draw vertices
    for (let i = 0; i < screenVertices.length; i++) {
      const v = screenVertices[i];
      if (!v) continue;

      ctx.beginPath();
      ctx.arc(v.x, v.y, VERTEX_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = i === 0 ? '#16a34a' : '#22c55e';
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Draw cursor position if drawing
    if (isDrawing && currentPos) {
      const screenCurrent = worldToScreen(currentPos.x, currentPos.y);
      ctx.beginPath();
      ctx.arc(screenCurrent.x, screenCurrent.y, VERTEX_RADIUS - 2, 0, Math.PI * 2);
      ctx.fillStyle = '#86efac';
      ctx.fill();
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  };

  // Calculate centroid of screen vertices
  const calculateCentroid = (vertices: Array<{ x: number; y: number }>): { x: number; y: number } => {
    if (vertices.length === 0) return { x: 0, y: 0 };
    const sum = vertices.reduce((acc, v) => ({ x: acc.x + v.x, y: acc.y + v.y }), { x: 0, y: 0 });
    return { x: sum.x / vertices.length, y: sum.y / vertices.length };
  };

  // Mouse handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Right click for panning
      if (e.button === 2) {
        e.preventDefault();
        setIsPanning(true);
        setLastPanPos({ x: e.clientX, y: e.clientY });
        return;
      }

      // Left click for drawing
      if (drawingTool === 'polygon' || drawingTool === 'rectangle') {
        const worldPoint = screenToWorld(x, y);
        const snappedPoint = snapPoint(worldPoint);

        if (!drawing.isDrawing) {
          startDrawing();
          addDrawingVertex(snappedPoint);
        } else {
          // Check if clicking near first vertex to close
          if (drawing.vertices.length >= 3) {
            const firstVertex = drawing.vertices[0];
            if (firstVertex) {
              const dist = Math.sqrt(
                Math.pow(snappedPoint.x - firstVertex.x, 2) +
                  Math.pow(snappedPoint.y - firstVertex.y, 2)
              );
              if (dist < snapSettings.vertexDistance) {
                closeDrawing();
                onFootprintComplete?.(drawing.vertices);
                return;
              }
            }
          }
          addDrawingVertex(snappedPoint);
        }
      }
    },
    [
      drawingTool,
      drawing,
      screenToWorld,
      snapPoint,
      startDrawing,
      addDrawingVertex,
      closeDrawing,
      snapSettings.vertexDistance,
      onFootprintComplete,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Handle panning
      if (isPanning) {
        const dx = e.clientX - lastPanPos.x;
        const dy = e.clientY - lastPanPos.y;
        setViewOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        setLastPanPos({ x: e.clientX, y: e.clientY });
        return;
      }

      // Update preview position
      if (drawing.isDrawing) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const worldPoint = screenToWorld(x, y);
        const snappedPoint = snapPoint(worldPoint);
        updateDrawingPreview(snappedPoint);
      }
    },
    [isPanning, lastPanPos, drawing.isDrawing, screenToWorld, snapPoint, updateDrawingPreview]
  );

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => Math.max(5, Math.min(100, prev * delta)));
  }, []);

  const handleDoubleClick = useCallback(() => {
    if (drawing.isDrawing && drawing.vertices.length >= 3) {
      closeDrawing();
      onFootprintComplete?.(drawing.vertices);
    }
  }, [drawing, closeDrawing, onFootprintComplete]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        cancelDrawing();
      } else if (e.key === 'Enter' && drawing.vertices.length >= 3) {
        closeDrawing();
        onFootprintComplete?.(drawing.vertices);
      }
    },
    [cancelDrawing, closeDrawing, drawing.vertices.length, onFootprintComplete]
  );

  // Touch handlers for mobile support
  const getTouchPoints = useCallback((e: React.TouchEvent): Array<{ x: number; y: number }> => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return [];
    return Array.from(e.touches).map((touch) => ({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }));
  }, []);

  const getTouchDistance = useCallback((touches: Array<{ x: number; y: number }>): number => {
    if (touches.length < 2) return 0;
    const t0 = touches[0];
    const t1 = touches[1];
    if (!t0 || !t1) return 0;
    return Math.sqrt(Math.pow(t1.x - t0.x, 2) + Math.pow(t1.y - t0.y, 2));
  }, []);

  const getTouchCenter = useCallback((touches: Array<{ x: number; y: number }>): { x: number; y: number } => {
    if (touches.length === 0) return { x: 0, y: 0 };
    const sum = touches.reduce((acc, t) => ({ x: acc.x + t.x, y: acc.y + t.y }), { x: 0, y: 0 });
    return { x: sum.x / touches.length, y: sum.y / touches.length };
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      const touches = getTouchPoints(e);

      if (touches.length === 1) {
        // Single touch - could be drawing or pan start
        setTouchState({
          startTouches: touches,
          lastTouches: touches,
          initialScale: scale,
          initialOffset: viewOffset,
        });
      } else if (touches.length === 2) {
        // Two fingers - pinch/pan gesture
        e.preventDefault();
        setTouchState({
          startTouches: touches,
          lastTouches: touches,
          initialScale: scale,
          initialOffset: viewOffset,
        });
      }
    },
    [getTouchPoints, scale, viewOffset]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!touchState) return;

      const touches = getTouchPoints(e);

      if (touches.length === 2 && touchState.startTouches.length === 2) {
        // Two-finger gesture: pinch to zoom and pan
        e.preventDefault();

        const startDist = getTouchDistance(touchState.startTouches);
        const currentDist = getTouchDistance(touches);

        if (startDist > 0 && currentDist > 0) {
          // Pinch zoom
          const scaleRatio = currentDist / startDist;
          const newScale = Math.max(5, Math.min(100, touchState.initialScale * scaleRatio));
          setScale(newScale);
        }

        // Pan
        const lastCenter = getTouchCenter(touchState.lastTouches);
        const currentCenter = getTouchCenter(touches);
        const dx = currentCenter.x - lastCenter.x;
        const dy = currentCenter.y - lastCenter.y;

        setViewOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
        setTouchState((prev) => (prev ? { ...prev, lastTouches: touches } : null));
      } else if (touches.length === 1 && touchState.startTouches.length === 1) {
        // Single finger move - update drawing preview
        const touch = touches[0];
        if (drawing.isDrawing && touch) {
          const worldPoint = screenToWorld(touch.x, touch.y);
          const snappedPoint = snapPoint(worldPoint);
          updateDrawingPreview(snappedPoint);
        }
        setTouchState((prev) => (prev ? { ...prev, lastTouches: touches } : null));
      }
    },
    [
      touchState,
      getTouchPoints,
      getTouchDistance,
      getTouchCenter,
      drawing.isDrawing,
      screenToWorld,
      snapPoint,
      updateDrawingPreview,
    ]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLCanvasElement>) => {
      if (!touchState) return;

      const remainingTouches = getTouchPoints(e);

      // Check if this was a tap (single touch, minimal movement)
      if (
        touchState.startTouches.length === 1 &&
        remainingTouches.length === 0 &&
        e.changedTouches.length === 1
      ) {
        const startTouch = touchState.startTouches[0];
        const rect = canvasRef.current?.getBoundingClientRect();
        if (startTouch && rect) {
          const endX = e.changedTouches[0]?.clientX ?? 0;
          const endY = e.changedTouches[0]?.clientY ?? 0;
          const endTouch = { x: endX - rect.left, y: endY - rect.top };

          const moveDist = Math.sqrt(
            Math.pow(endTouch.x - startTouch.x, 2) + Math.pow(endTouch.y - startTouch.y, 2)
          );

          // If minimal movement, treat as tap for drawing
          if (moveDist < 10 && (drawingTool === 'polygon' || drawingTool === 'rectangle')) {
            const worldPoint = screenToWorld(endTouch.x, endTouch.y);
            const snappedPoint = snapPoint(worldPoint);

            if (!drawing.isDrawing) {
              startDrawing();
              addDrawingVertex(snappedPoint);
            } else {
              // Check if tapping near first vertex to close
              if (drawing.vertices.length >= 3) {
                const firstVertex = drawing.vertices[0];
                if (firstVertex) {
                  const dist = Math.sqrt(
                    Math.pow(snappedPoint.x - firstVertex.x, 2) +
                      Math.pow(snappedPoint.y - firstVertex.y, 2)
                  );
                  if (dist < snapSettings.vertexDistance) {
                    closeDrawing();
                    onFootprintComplete?.(drawing.vertices);
                    setTouchState(null);
                    return;
                  }
                }
              }
              addDrawingVertex(snappedPoint);
            }
          }
        }
      }

      // Reset touch state when all fingers lifted
      if (remainingTouches.length === 0) {
        setTouchState(null);
      } else {
        setTouchState((prev) => (prev ? { ...prev, lastTouches: remainingTouches } : null));
      }
    },
    [
      touchState,
      getTouchPoints,
      drawingTool,
      drawing,
      screenToWorld,
      snapPoint,
      startDrawing,
      addDrawingVertex,
      closeDrawing,
      snapSettings.vertexDistance,
      onFootprintComplete,
    ]
  );

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border bg-card px-4 py-2">
        <div className="flex items-center gap-2" role="group" aria-label="Drawing tools">
          <span className="text-sm font-medium text-muted-foreground">Tools:</span>
          <button
            onClick={() => setDrawingTool('select')}
            className={`rounded px-3 py-1 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
              drawingTool === 'select'
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
            aria-pressed={drawingTool === 'select'}
          >
            Select
          </button>
          <button
            onClick={() => setDrawingTool('polygon')}
            className={`rounded px-3 py-1 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
              drawingTool === 'polygon'
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
            aria-pressed={drawingTool === 'polygon'}
          >
            Draw Polygon
          </button>
          <button
            onClick={() => setDrawingTool('rectangle')}
            className={`rounded px-3 py-1 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
              drawingTool === 'rectangle'
                ? 'bg-primary/10 text-primary'
                : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
            }`}
            aria-pressed={drawingTool === 'rectangle'}
          >
            Draw Rectangle
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* Snap settings */}
          <div className="flex items-center gap-2" role="group" aria-label="Snap settings">
            <span className="text-xs text-muted-foreground">Snap:</span>
            <label className="flex cursor-pointer items-center gap-1">
              <input
                type="checkbox"
                checked={snapSettings.grid}
                onChange={(e) => setSnapSettings({ grid: e.target.checked })}
                className="h-3 w-3 rounded border-border text-primary focus:ring-primary"
                aria-label="Snap to grid"
              />
              <span className="text-xs text-foreground">Grid</span>
            </label>
            <label className="flex cursor-pointer items-center gap-1">
              <input
                type="checkbox"
                checked={snapSettings.vertex}
                onChange={(e) => setSnapSettings({ vertex: e.target.checked })}
                className="h-3 w-3 rounded border-border text-primary focus:ring-primary"
                aria-label="Snap to vertex"
              />
              <span className="text-xs text-foreground">Vertex</span>
            </label>
          </div>

          {/* Actions */}
          {drawing.isDrawing && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => cancelDrawing()}
                className="rounded bg-destructive/10 px-2 py-1 text-xs text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1"
              >
                Cancel
              </button>
              {drawing.vertices.length >= 3 && (
                <button
                  onClick={() => {
                    closeDrawing();
                    onFootprintComplete?.(drawing.vertices);
                  }}
                  className="rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                >
                  Complete
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Canvas */}
      <div ref={containerRef} className="relative flex-1 overflow-hidden">
        <canvas
          ref={canvasRef}
          width={canvasSize.width}
          height={canvasSize.height}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onContextMenu={(e) => e.preventDefault()}
          tabIndex={0}
          className="cursor-crosshair outline-none touch-none"
          style={{ touchAction: 'none' }}
        />

        {/* Floor level indicator */}
        <div className="absolute left-4 top-4 rounded-md border border-border bg-card px-3 py-1.5 shadow-sm">
          <span className="text-sm text-foreground">
            Floor: <span className="font-medium">{floorLevels[activeFloorIndex]?.name ?? 'Level 1'}</span>
            <span className="ml-1 text-muted-foreground">(Z = {floorZ}m)</span>
          </span>
        </div>

        {/* Status bar */}
        <div className="absolute bottom-4 left-4 rounded-md border border-border bg-card px-3 py-1.5 text-xs shadow-sm" role="status" aria-live="polite">
          {drawing.isDrawing ? (
            <span className="text-foreground">
              <span className="font-medium text-primary">Drawing:</span> {drawing.vertices.length} vertices
              {drawing.currentPosition && (
                <span className="ml-2 font-mono text-muted-foreground">
                  ({drawing.currentPosition.x.toFixed(2)}, {drawing.currentPosition.y.toFixed(2)})
                </span>
              )}
              <span className="ml-2 text-muted-foreground">• Double-click or Enter to close</span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{floorZones.length}</span> zones on this floor
              <span className="mx-2">•</span>
              <span className="hidden md:inline">Scroll to zoom • Right-drag to pan</span>
              <span className="inline md:hidden">Pinch to zoom • Two-finger drag to pan</span>
            </span>
          )}
        </div>

        {/* Touch/Mobile hint overlay */}
        <div className="absolute right-4 top-4 flex flex-col gap-1 rounded-md border border-border bg-card/95 px-3 py-2 text-xs text-muted-foreground shadow-sm backdrop-blur-sm md:hidden">
          <div className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
            </svg>
            <span>Tap to place points</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
            </svg>
            <span>Pinch to zoom</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
            </svg>
            <span>Two fingers to pan</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FloorPlanEditor;
